import { type FormEvent, type ReactNode, useState } from 'react'
import { useLocation } from '../../../features/locations/use-location'
import {
  canEditWanSettings,
  useSaveWanSettings,
  useWanSettings,
  type WanDataRecord,
  type WanSettingsData,
  type WanSettingsUpdate,
} from '../../../features/locations/use-wan-settings'
import { ApiError } from '../../../lib/api-client'
import { useIdOrCode } from '../../use-id-or-code'
import { Bar, Crumbs, mono, Note, Panel, SkelPanels, StatusText } from './ui'

const inputCls =
  'h-7 w-full rounded-[4px] border border-[var(--color-border)] bg-[var(--color-bg)] px-2 text-[12px] text-[var(--color-fg)] placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50'
const th =
  'h-7 border-b border-[var(--color-border)] px-3 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--color-muted)]'
const td = 'h-7 truncate border-b border-[var(--color-border)] px-3 text-[12px]'

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the control is passed as children inside this label
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-[var(--color-muted)]">{label}</span>
      {children}
    </label>
  )
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return 'Forbidden: you lack permission to change WAN settings for this store.'
    if (e.status === 400) return 'Rejected (400): check the values and that the store is PROVISIONED.'
    return `Save failed (${e.status}). Try again.`
  }
  return 'Save failed. Try again.'
}

function SaveBar({
  ok,
  dirty,
  pending,
  error,
  success,
  onReset,
}: {
  ok: boolean
  dirty: boolean
  pending: boolean
  error: unknown
  success: boolean
  onReset: () => void
}) {
  return (
    <div className="flex h-9 items-center gap-2 border-t border-[var(--color-border)] px-3">
      <button
        type="submit"
        disabled={!ok || !dirty || pending}
        className="h-6 rounded-[4px] bg-[var(--color-accent)] px-2.5 text-[12px] font-medium text-[var(--color-accent-fg)] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {pending ? 'Saving...' : 'Save'}
      </button>
      <button
        type="button"
        onClick={onReset}
        disabled={!dirty || pending}
        className="h-6 rounded-[4px] border border-[var(--color-border)] px-2 text-[12px] hover:bg-[var(--color-surface-2)] disabled:cursor-not-allowed disabled:opacity-40"
      >
        Reset
      </button>
      <div aria-live="polite" className="text-[12px]" style={mono}>
        {error ? (
          <span style={{ color: 'var(--v2-bad, #f0616d)' }}>{errorMessage(error)}</span>
        ) : success && !dirty ? (
          <span style={{ color: 'var(--v2-ok)' }}>saved</span>
        ) : null}
      </div>
    </div>
  )
}

interface MerakiRow {
  enabled: boolean
  mode: 'static' | 'dynamic' | ''
  address: string
  gateway: string
  carrier: string
}

const MERAKI_CARRIER_TYPE: Record<string, WanDataRecord['type']> = { wan1: 'PRIMARY', wan2: 'SECONDARY' }

function MerakiForm({
  locationId,
  data,
  interfaces,
  canEdit,
}: {
  locationId: string
  data: WanSettingsData
  interfaces: Record<
    string,
    { enabled?: boolean; assignmentMode?: 'static' | 'dynamic'; address?: string; gateway?: string }
  >
  canEdit: boolean
}) {
  const save = useSaveWanSettings()
  const initial: Record<string, MerakiRow> = Object.fromEntries(
    Object.entries(interfaces).map(([name, i]) => [
      name,
      {
        enabled: i.enabled ?? false,
        mode: i.assignmentMode ?? '',
        address: i.address ?? '',
        gateway: i.gateway ?? '',
        carrier: data.dbRecords.find((r) => r.type === MERAKI_CARRIER_TYPE[name])?.carrier ?? '',
      },
    ]),
  )
  const [rows, setRows] = useState(initial)
  const patch = (name: string, p: Partial<MerakiRow>) => {
    save.reset()
    setRows((r) => ({ ...r, [name]: { ...r[name], ...p } as MerakiRow }))
  }
  const names = Object.keys(rows)
  const changed = names.filter((n) => JSON.stringify(rows[n]) !== JSON.stringify(initial[n]))
  const valid = !names.some((n) => {
    const r = rows[n]
    return r?.mode === 'static' && (!r.address.trim() || !r.gateway.trim())
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const body: WanSettingsUpdate = { vendor: 'meraki', interfaces: {} }
    for (const name of changed) {
      const r = rows[name] as MerakiRow
      body.interfaces[name] = {
        enabled: r.enabled,
        ...(r.carrier.trim() ? { carrier: r.carrier.trim() } : {}),
        ...(r.mode === 'static'
          ? {
              svis: {
                ipv4: { assignmentMode: 'static' as const, address: r.address.trim(), gateway: r.gateway.trim() },
              },
            }
          : r.mode === 'dynamic'
            ? { svis: { ipv4: { assignmentMode: 'dynamic' as const } } }
            : {}),
      }
    }
    save.mutate({ locationId, body })
  }

  return (
    <form onSubmit={submit}>
      {names.length === 0 ? (
        <Note>No interfaces reported by Meraki.</Note>
      ) : (
        names.map((name) => {
          const r = rows[name] as MerakiRow
          return (
            <fieldset
              key={name}
              disabled={!canEdit || save.isPending}
              className="border-b border-[var(--color-border)] px-3 py-2"
            >
              <legend className="sr-only">{name}</legend>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[12px] font-medium uppercase" style={mono}>
                  {name}
                </span>
                <label className="flex items-center gap-1.5 text-[12px]">
                  <input
                    type="checkbox"
                    checked={r.enabled}
                    onChange={(e) => patch(name, { enabled: e.target.checked })}
                    className="size-3.5 accent-[var(--color-accent)]"
                  />
                  Enabled
                </label>
              </div>
              <div className="grid gap-2 md:grid-cols-4">
                <Field label="Addressing">
                  <select
                    className={inputCls}
                    value={r.mode}
                    onChange={(e) => patch(name, { mode: e.target.value as MerakiRow['mode'] })}
                  >
                    <option value="">Unchanged</option>
                    <option value="dynamic">DHCP</option>
                    <option value="static">Static</option>
                  </select>
                </Field>
                <Field label="Address">
                  <input
                    className={inputCls}
                    style={mono}
                    value={r.address}
                    disabled={r.mode !== 'static'}
                    onChange={(e) => patch(name, { address: e.target.value })}
                    placeholder="203.0.113.10/30"
                  />
                </Field>
                <Field label="Gateway">
                  <input
                    className={inputCls}
                    style={mono}
                    value={r.gateway}
                    disabled={r.mode !== 'static'}
                    onChange={(e) => patch(name, { gateway: e.target.value })}
                    placeholder="203.0.113.9"
                  />
                </Field>
                <Field label="Carrier">
                  <input
                    className={inputCls}
                    value={r.carrier}
                    onChange={(e) => patch(name, { carrier: e.target.value })}
                    placeholder="e.g. Comcast"
                  />
                </Field>
              </div>
              {r.mode === 'static' && (!r.address.trim() || !r.gateway.trim()) && (
                <p className="mt-1.5 text-[11px]" style={{ color: 'var(--v2-warn, #e5a23c)' }}>
                  Static needs address and gateway.
                </p>
              )}
            </fieldset>
          )
        })
      )}
      <SaveBar
        ok={canEdit && valid}
        dirty={changed.length > 0}
        pending={save.isPending}
        error={save.error}
        success={save.isSuccess}
        onReset={() => {
          save.reset()
          setRows(initial)
        }}
      />
    </form>
  )
}

interface CfWan {
  id?: string
  name?: string
  static_addressing?: { address: string; gateway_address: string; secondary_address?: string }
}

type CfWithId = CfWan & { id: string }

function CloudflareForm({
  locationId,
  data,
  wans,
  canEdit,
}: {
  locationId: string
  data: WanSettingsData
  wans: CfWan[]
  canEdit: boolean
}) {
  const editable = wans.filter((w): w is CfWithId => typeof w.id === 'string')
  const [wanId, setWanId] = useState(editable[0]?.id ?? '')
  if (editable.length === 0) return <Note>No editable Cloudflare WANs found.</Note>
  const wan = editable.find((w) => w.id === wanId)
  return (
    <CloudflareFields
      key={wanId}
      locationId={locationId}
      editable={editable}
      wanId={wanId}
      onWan={setWanId}
      initial={{
        address: wan?.static_addressing?.address ?? '',
        gateway: wan?.static_addressing?.gateway_address ?? '',
        secondary: wan?.static_addressing?.secondary_address ?? '',
        carrier: data.dbRecords[0]?.carrier ?? '',
      }}
      canEdit={canEdit}
    />
  )
}

function CloudflareFields({
  locationId,
  editable,
  wanId,
  onWan,
  initial,
  canEdit,
}: {
  locationId: string
  editable: CfWithId[]
  wanId: string
  onWan: (id: string) => void
  initial: { address: string; gateway: string; secondary: string; carrier: string }
  canEdit: boolean
}) {
  const save = useSaveWanSettings()
  const [v, setV] = useState(initial)
  const set = (p: Partial<typeof v>) => {
    save.reset()
    setV((x) => ({ ...x, ...p }))
  }
  const dirty = JSON.stringify(v) !== JSON.stringify(initial)
  const valid = v.address.trim() !== '' && v.gateway.trim() !== ''

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate({
          locationId,
          body: {
            vendor: 'cloudflare',
            wanId,
            ...(v.carrier.trim() ? { carrier: v.carrier.trim() } : {}),
            staticAddressing: {
              address: v.address.trim(),
              gateway_address: v.gateway.trim(),
              ...(v.secondary.trim() ? { secondary_address: v.secondary.trim() } : {}),
            },
          },
        })
      }}
    >
      <fieldset disabled={!canEdit || save.isPending} className="px-3 py-2">
        <div className="grid gap-2 md:grid-cols-3">
          <Field label="WAN">
            <select className={inputCls} value={wanId} onChange={(e) => onWan(e.target.value)}>
              {editable.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name ?? w.id}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Carrier">
            <input
              className={inputCls}
              value={v.carrier}
              onChange={(e) => set({ carrier: e.target.value })}
              placeholder="e.g. Comcast"
            />
          </Field>
          <span className="hidden md:block" />
          <Field label="Address">
            <input
              className={inputCls}
              style={mono}
              value={v.address}
              onChange={(e) => set({ address: e.target.value })}
              placeholder="203.0.113.10/30"
            />
          </Field>
          <Field label="Gateway">
            <input
              className={inputCls}
              style={mono}
              value={v.gateway}
              onChange={(e) => set({ gateway: e.target.value })}
              placeholder="203.0.113.9"
            />
          </Field>
          <Field label="Secondary (optional)">
            <input
              className={inputCls}
              style={mono}
              value={v.secondary}
              onChange={(e) => set({ secondary: e.target.value })}
            />
          </Field>
        </div>
        {!valid && (
          <p className="mt-1.5 text-[11px]" style={{ color: 'var(--v2-warn, #e5a23c)' }}>
            Address and gateway are required.
          </p>
        )}
      </fieldset>
      <SaveBar
        ok={canEdit && valid}
        dirty={dirty}
        pending={save.isPending}
        error={save.error}
        success={save.isSuccess}
        onReset={() => {
          save.reset()
          setV(initial)
        }}
      />
    </form>
  )
}

export function SignalWanSettings() {
  const idOrCode = useIdOrCode()
  const loc = useLocation(idOrCode)
  const { data, isLoading, error } = useWanSettings(idOrCode)
  const locationId = loc.data?.id

  const crumbs = <Crumbs idOrCode={idOrCode} trail="wan" />

  if (isLoading || loc.isLoading)
    return (
      <div className="flex h-full flex-col">
        <Bar crumbs={crumbs} title="WAN settings" />
        <div className="overflow-auto">
          <SkelPanels n={2} />
        </div>
      </div>
    )
  if (error || loc.error || !data || !locationId)
    return (
      <div className="flex h-full flex-col">
        <Bar crumbs={crumbs} title="WAN settings" />
        <Note error>
          {error || loc.error ? `Failed to load WAN settings: ${(error ?? loc.error)?.message}` : 'Location not found.'}
        </Note>
      </div>
    )

  const canEdit = canEditWanSettings(data)
  const vs = data.vendorState

  return (
    <div className="flex h-full flex-col">
      <Bar
        crumbs={crumbs}
        title="WAN settings"
        meta={
          data.lifecycle && (
            <span className="text-[12px]">
              <StatusText status={data.lifecycle} />
            </span>
          )
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="grid gap-3">
          {!canEdit && (
            <output
              className="block px-3 py-2 text-[12px]"
              style={{ boxShadow: 'inset 2px 0 0 var(--v2-warn, #e5a23c)', background: 'var(--color-surface)' }}
            >
              Read-only: editing requires lifecycle PROVISIONED
              {data.lifecycle ? ` (currently ${data.lifecycle})` : ''}.
            </output>
          )}

          <Panel
            title="Circuits"
            aside={
              data.routerSerial ? (
                <span className="text-[var(--color-muted)]" style={mono}>
                  {data.routerSerial}
                </span>
              ) : undefined
            }
          >
            {data.dbRecords.length === 0 ? (
              <Note>No circuits recorded for this store.</Note>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] border-collapse" aria-label="Circuits">
                  <thead>
                    <tr>
                      {['Circuit', 'Carrier', 'Addr', 'IP', 'Gateway', 'Down / Up', 'Status'].map((h) => (
                        <th key={h} scope="col" className={th}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.dbRecords.map((r) => (
                      <tr key={r.id} className="hover:bg-[var(--color-surface-2)]">
                        <td className={td} style={mono}>
                          {r.type.toLowerCase()}
                        </td>
                        <td className={td}>{r.carrier || '-'}</td>
                        <td className={td}>{r.assignmentType === 'STATIC' ? 'Static' : 'DHCP'}</td>
                        <td className={td} style={mono}>
                          {r.assignedCidr ?? r.assignedIp ?? '-'}
                        </td>
                        <td className={td} style={mono}>
                          {r.gw ?? '-'}
                        </td>
                        <td className={td} style={mono}>
                          {r.rxSpeed ?? '-'} / {r.txSpeed ?? '-'}
                        </td>
                        <td className={td}>
                          <StatusText status={r.status ? 'UP' : 'DOWN'} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title={`Edit${vs.vendor === 'unsupported' ? '' : ` · ${vs.vendor}`}`}>
            {vs.vendor === 'meraki' ? (
              <MerakiForm
                key={JSON.stringify(vs.interfaces)}
                locationId={locationId}
                data={data}
                interfaces={vs.interfaces ?? {}}
                canEdit={canEdit}
              />
            ) : vs.vendor === 'cloudflare' ? (
              <CloudflareForm
                key={JSON.stringify(vs.wans)}
                locationId={locationId}
                data={data}
                wans={vs.wans ?? []}
                canEdit={canEdit}
              />
            ) : (
              <Note>
                Edge device ({data.edgeVendor ?? 'unknown vendor'}) does not support editing WAN settings here.
              </Note>
            )}
          </Panel>
        </div>
      </div>
    </div>
  )
}
